/**
 * The MEMBER-facing VIEWER RPC definitions (OL-242).
 *
 * This file is **contracts only**. It declares the `loops.viewer.*` group and
 * the DTOs it carries; it mounts no handler, writes no row and adds no
 * migration. The handlers ticket merges the group into `OrcaloopsRpcs` and
 * provides the router layer — until then nothing here changes runtime
 * behaviour, and that is deliberate: the web tickets need a contract to compile
 * against before the read path exists.
 *
 * **Every DTO is a wire type of a `@fm/db` model, with two exceptions.**
 * `LoopDecision.json`, `LoopInboxEvent.json` and `LoopWake.json` cross the wire
 * verbatim — the models are the single source of truth and are never redeclared
 * here. The executor is one exception: `loop_executors` carries two SECRETS
 * (`deferredToken`, `callbackTokenHash`), so it is PROJECTED through
 * {@link LoopExecutorView}, which names every column it exposes. A fan-out child
 * is the other: its invocation row carries `parentDeferredToken`, so it is
 * projected through {@link LoopInvocationChild}. A projection —
 * rather than an omission — is what makes a future secret column fail closed:
 * a column added to the model is simply absent from the view until somebody
 * adds it by hand, and `loop-viewer.rpc.test.ts` fails if either secret ever
 * appears.
 *
 * **No payload carries a `customerId`.** The customer is resolved server-side
 * from the actor by `resolveActorCustomer` (`loop-scope.ts`) in the handlers
 * ticket, so "trust the customer id on the wire" is unrepresentable here rather
 * than merely unchecked. Every rpc carries {@link LoopActorMiddleware}.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { LoopDecision } from "@fm/db/models/LoopDecision";
import { LoopDocument } from "@fm/db/models/LoopDocument";
import { LoopExecutorKind, LoopExitEncoded, LoopPresence } from "@fm/db/models/LoopExecutor";
import { LoopInboxEvent } from "@fm/db/models/LoopInboxEvent";
import {
  LoopEngineMode,
  LoopInvocationStatus,
  LoopWalkStateEncoded,
  OutcomeBodyEncoded,
} from "@fm/db/models/LoopInvocation";
import { LoopWake } from "@fm/db/models/LoopWake";
import { Schema } from "effect";

import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValidationError } from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

/**
 * One executor-ledger row as the viewer sees it.
 *
 * A hand-named PROJECTION of `@fm/db/models/LoopExecutor`, not the model's
 * `json` variant, because the row carries two secret columns — `deferredToken`
 * (the bearer token a deferred executor redeems) and `callbackTokenHash` — and
 * NEITHER may ever leave the server. `customerId` and `invocationId` are also
 * dropped: the caller already addressed the invocation, and the customer is the
 * actor's, never the payload's.
 *
 * @since 1.0.0
 */
export const LoopExecutorView = Schema.Struct({
  id: Schema.String,
  node: Schema.String,
  claim: Schema.String,
  kind: LoopExecutorKind,
  loopRef: Schema.NullOr(Schema.String),
  harness: Schema.NullOr(Schema.String),
  dispatchedAt: Schema.NullOr(Schema.DateTimeUtc),
  ackedAt: Schema.NullOr(Schema.DateTimeUtc),
  lastProbeAt: Schema.NullOr(Schema.DateTimeUtc),
  lastHeartbeatAt: Schema.NullOr(Schema.DateTimeUtc),
  concludedAt: Schema.NullOr(Schema.DateTimeUtc),
  killTerminalAt: Schema.NullOr(Schema.DateTimeUtc),
  externalRef: Schema.NullOr(Schema.String),
  runnerId: Schema.NullOr(Schema.String),
  lastPresence: Schema.NullOr(LoopPresence),
  exit: Schema.NullOr(LoopExitEncoded),
  killStep: Schema.Number,
  detail: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
});
export type LoopExecutorView = Schema.Schema.Type<typeof LoopExecutorView>;

/**
 * One child run a fan-out spawned under the viewed invocation — enough to name,
 * colour and link each fanned-out issue from the parent's run page.
 *
 * A hand-named PROJECTION of `@fm/db/models/LoopInvocation`, like
 * {@link LoopExecutorView}: the child row carries `parentDeferredToken` (a
 * redeemable secret) and its `customerId`, and neither may leave the server.
 * `parentInvocationId` is dropped too — the caller already addressed the parent.
 *
 * `node` is the parent node the child runs under (`""` when the row names none)
 * and `label` is what the page shows: the Linear issue the child stands for,
 * else its fan-out instance, else the head of its id.
 *
 * @since 1.0.0
 */
export const LoopInvocationChild = Schema.Struct({
  id: Schema.String,
  loopId: Schema.String,
  loopSlug: Schema.String,
  node: Schema.String,
  instanceId: Schema.NullOr(Schema.String),
  label: Schema.String,
  status: LoopInvocationStatus,
  createdAt: Schema.DateTimeUtc,
  concludedAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type LoopInvocationChild = Schema.Schema.Type<typeof LoopInvocationChild>;

/**
 * The compact invocation row the viewer's list renders — enough to name, sort
 * and colour an invocation without paying for its decision log.
 *
 * `cursorNode` is not a column: the handler reads it from `state.cursor.node`
 * (the committed walk state is a structurally-opaque record —
 * `@fm/db/models/LoopInvocation`'s `LoopWalkStateEncoded`), and it is `null`
 * before the first commit.
 *
 * @since 1.0.0
 */
export const LoopInvocationSummary = Schema.Struct({
  id: Schema.String,
  loopId: Schema.String,
  loopVersion: Schema.Number,
  loopSlug: Schema.String,
  status: LoopInvocationStatus,
  mode: LoopEngineMode,
  triggerKey: Schema.NullOr(Schema.String),
  cursorNode: Schema.NullOr(Schema.String),
  stateSeq: Schema.Number,
  pendingReason: Schema.NullOr(Schema.String),
  stuckReason: Schema.NullOr(Schema.String),
  supersedesInvocationId: Schema.NullOr(Schema.String),
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
  concludedAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type LoopInvocationSummary = Schema.Schema.Type<typeof LoopInvocationSummary>;

/**
 * Everything the viewer needs to render ONE invocation: its summary row, the
 * inputs it was rendered from, the frozen `document` of the loop version it
 * actually ran, the committed walk state, the four append-only ledgers —
 * decisions, executors, inbox events and wake ticks — and the child runs its
 * fan-outs spawned.
 *
 * The same shape is the success type of both the one-shot read and the stream,
 * so a subscriber re-renders from a whole snapshot and never has to merge
 * deltas.
 *
 * @since 1.0.0
 */
export const LoopInvocationView = Schema.Struct({
  invocation: LoopInvocationSummary,
  inputs: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
  document: LoopDocument,
  state: Schema.NullOr(LoopWalkStateEncoded),
  decisions: Schema.Array(LoopDecision.json),
  executors: Schema.Array(LoopExecutorView),
  events: Schema.Array(LoopInboxEvent.json),
  wakes: Schema.Array(LoopWake.json),
  /** The fan-out's child runs, oldest first (ties by id). Empty for a leaf run. */
  children: Schema.Array(LoopInvocationChild),
  outcomeHead: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  /**
   * The concluded outcome's body as stored — an `Inline` value or an opaque
   * `Ref`. A `Ref` is never dereferenced here; `loops.invocationOutcome` reads it.
   */
  outcomeBody: Schema.NullOr(OutcomeBodyEncoded),
});
export type LoopInvocationView = Schema.Schema.Type<typeof LoopInvocationView>;

/**
 * List the invocations of one loop definition. `limit` is optional; the handler
 * clamps it. No `customerId` — the actor's scope is the only scope.
 *
 * @since 1.0.0
 */
export const LoopViewerInvocationsInput = Schema.Struct({
  loopId: Schema.String,
  limit: Schema.optional(Schema.Int),
});
export type LoopViewerInvocationsInput = Schema.Schema.Type<typeof LoopViewerInvocationsInput>;

/**
 * Address ONE invocation by id, for the one-shot read and for the stream.
 *
 * @since 1.0.0
 */
export const LoopViewerInvocationInput = Schema.Struct({
  invocationId: Schema.String,
});
export type LoopViewerInvocationInput = Schema.Schema.Type<typeof LoopViewerInvocationInput>;

/**
 * The member-facing viewer RPC group: three read-only rpcs, every one behind
 * {@link LoopActorMiddleware}.
 *
 * The group is deliberately NOT merged into `OrcaloopsRpcs` here — merging it
 * would demand handlers, which this ticket does not ship. The handlers ticket
 * does the `.merge(LoopViewerRpcs)` in `orcaloops-routes.ts` and provides the
 * router layer.
 *
 * @since 1.0.0
 */
export class LoopViewerRpcs extends RpcGroup.make(
  Rpc.make("loops.viewer.invocations", {
    payload: LoopViewerInvocationsInput,
    success: Schema.Array(LoopInvocationSummary),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.viewer.invocation", {
    payload: LoopViewerInvocationInput,
    success: LoopInvocationView,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.viewer.invocationStream", {
    payload: LoopViewerInvocationInput,
    success: LoopInvocationView,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
    stream: true,
  }).middleware(LoopActorMiddleware),
) {}
